import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star, ExternalLink, PenSquare } from 'lucide-react';

const GOOGLE_PLACE_URL = 'https://maps.google.com/?cid=8116667297370837121';
const LEAVE_REVIEW_URL = 'https://maps.google.com/?cid=8116667297370837121';

/**
 * Google Reviews highlight section.
 * Shows a big 5-star badge, average rating, and two clear CTAs:
 *  - "Read our Google Reviews" (opens Google reviews)
 *  - "Leave a Review" (opens Google review composer)
 */
export const GoogleReviewsSection = () => {
  const { t } = useTranslation();

  return (
    <section
      id="reviews"
      className="relative overflow-hidden bg-gradient-to-br from-[#0a0a0a] via-[#111] to-[#0a0a0a] py-20 md:py-24"
      data-testid="google-reviews-section"
    >
      {/* Gold radial glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.5), transparent 70%)' }}
      />

      <div className="container relative mx-auto max-w-4xl px-4 text-center">
        {/* Google G + kicker */}
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white ring-1 ring-white/20 backdrop-blur-sm">
          <svg className="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t('reviews.kicker')}
        </div>

        {/* 5 Stars big */}
        <div className="mb-4 flex items-center justify-center gap-1.5" data-testid="reviews-stars">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} className="h-8 w-8 md:h-10 md:w-10 fill-amber-400 text-amber-400 drop-shadow-[0_2px_8px_rgba(245,158,11,0.4)]" />
          ))}
        </div>

        <p className="mb-3 text-4xl font-bold text-white md:text-6xl">
          5.0
        </p>

        <h2 className="mb-3 font-serif text-2xl font-bold leading-tight text-white md:text-4xl">
          {t('reviews.title')}
        </h2>
        <p className="mx-auto mb-10 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg">
          {t('reviews.subtitle')}
        </p>

        {/* CTAs */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <a
            href={GOOGLE_PLACE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-gray-900 shadow-xl hover:bg-gray-100 hover:scale-[1.02] transition-all md:text-base"
            data-testid="read-google-reviews"
          >
            {t('reviews.read')}
            <ExternalLink className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
          <a
            href={LEAVE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full border-2 border-amber-400 bg-transparent px-6 py-3.5 text-sm font-semibold text-amber-300 hover:bg-amber-500 hover:text-black hover:scale-[1.02] transition-all md:text-base"
            data-testid="leave-google-review"
          >
            <PenSquare className="h-4 w-4" />
            {t('reviews.leave')}
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
